import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import { Button } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { AutoComplete } from 'primeng/autocomplete';

export interface WaitingListFormData {
  patient: string;
  desired_check_in: string;
  desired_check_out: string;
  priority: 1 | 2 | 3 | 4 | 5;
  notes?: string;
}

export interface PatientSearchResult {
  patient_id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone_primary?: string;
}

@Component({
  selector: 'lib-waiting-list-form-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    Dialog,
    Select,
    Textarea,
    Button,
    DatePicker,
    AutoComplete,
  ],
  templateUrl: './waiting-list-form-modal.component.html',
  styleUrls: ['./waiting-list-form-modal.component.css'],
})
export class WaitingListFormModalComponent implements OnInit, OnChanges {
  private fb = inject(FormBuilder);

  @Input() visible = false;
  @Input() editMode = false;
  @Input() waitlistId: string | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() formSubmit = new EventEmitter<WaitingListFormData>();
  @Output() searchPatients = new EventEmitter<string>();

  @Input() patientSearchResults: PatientSearchResult[] = [];
  @Input() selectedPatient: PatientSearchResult | null = null;

  waitingListForm!: FormGroup;
  filteredPatients: PatientSearchResult[] = [];
  minDate = new Date();

  priorities = [
    { labelKey: 'waitingList.priority.veryHigh', value: 1, color: '#ef4444' },
    { labelKey: 'waitingList.priority.high', value: 2, color: '#fb923c' },
    { labelKey: 'waitingList.priority.medium', value: 3, color: '#fbbf24' },
    { labelKey: 'waitingList.priority.low', value: 4, color: '#60a5fa' },
    { labelKey: 'waitingList.priority.veryLow', value: 5, color: '#9ca3af' },
  ];

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible && this.waitingListForm) {
      if (!this.editMode) {
        this.resetForm();
      }
    }

    if (changes['selectedPatient'] && this.selectedPatient && this.waitingListForm) {
      this.waitingListForm.patchValue({
        patient: this.selectedPatient,
      });
    }

    // Actualizar filteredPatients cuando cambien los resultados de búsqueda
    if (changes['patientSearchResults'] && this.patientSearchResults) {
      console.log(
        '📥 Resultados de búsqueda recibidos:',
        this.patientSearchResults.length,
        'pacientes',
      );
      console.log('📥 Primeros resultados:', this.patientSearchResults.slice(0, 2));
      this.filteredPatients = [...this.patientSearchResults];
    }
  }

  initializeForm(): void {
    this.waitingListForm = this.fb.group({
      patient: [null, Validators.required],
      desired_check_in: ['', Validators.required],
      desired_check_out: ['', Validators.required],
      priority: [2, Validators.required],
      notes: [''],
    });
  }

  onPatientSearch(event: { query: string }): void {
    const query = event.query;
    console.log('🔍 Buscando pacientes con query:', query);

    if (query && query.length >= 2) {
      this.searchPatients.emit(query);
      // Los resultados se actualizarán en ngOnChanges cuando el padre actualice patientSearchResults
      console.log('📡 Búsqueda emitida al componente padre');
    } else {
      this.filteredPatients = [];
      console.log('⚠️ Query muy corto, limpiando filteredPatients');
    }
  }

  onPatientSelect(event: { value?: PatientSearchResult } | PatientSearchResult): void {
    console.log('👤 Paciente seleccionado - evento completo:', event);
    const patient = 'value' in event ? event.value : event;
    console.log('👤 Objeto paciente a guardar:', patient);

    if (patient && typeof patient === 'object' && 'patient_id' in patient && patient.patient_id) {
      this.waitingListForm.patchValue({
        patient: patient,
      });
      console.log('✅ Paciente guardado en el formulario');
    } else {
      console.error('❌ El evento no contiene un objeto paciente válido');
    }
  }

  getPatientDisplayName(patient: PatientSearchResult | null): string {
    if (!patient) return '';
    return `${patient.first_name} ${patient.last_name}${
      patient.email ? ` (${patient.email})` : ''
    }`;
  }

  onSubmit(): void {
    if (this.waitingListForm.invalid) {
      console.error('❌ Formulario inválido');
      Object.keys(this.waitingListForm.controls).forEach((key) => {
        const control = this.waitingListForm.get(key);
        if (control?.invalid) {
          console.error(`Campo inválido: ${key}`, control.errors);
        }
      });

      // Marcar todos los campos como touched para mostrar errores
      this.waitingListForm.markAllAsTouched();
      return;
    }

    const formValue = this.waitingListForm.getRawValue();
    console.log('📋 Valores del formulario RAW:', formValue);

    const patient = formValue.patient;

    // Validar que el paciente sea un objeto con patient_id
    if (!patient || typeof patient !== 'object' || !patient.patient_id) {
      console.error('❌ El paciente no es válido:', patient);
      this.waitingListForm.get('patient')?.setErrors({ invalid: true });
      return;
    }

    // Convertir fechas a strings formato YYYY-MM-DD
    let checkInStr: string;
    let checkOutStr: string;

    try {
      const checkIn = formValue.desired_check_in;
      const checkOut = formValue.desired_check_out;

      // Si ya es string, usarlo directamente
      if (typeof checkIn === 'string') {
        checkInStr = checkIn.split('T')[0];
      } else if (checkIn instanceof Date) {
        // Si es Date, convertirlo
        checkInStr = checkIn.toISOString().split('T')[0];
      } else {
        throw new Error('Formato de fecha check-in inválido');
      }

      if (typeof checkOut === 'string') {
        checkOutStr = checkOut.split('T')[0];
      } else if (checkOut instanceof Date) {
        checkOutStr = checkOut.toISOString().split('T')[0];
      } else {
        throw new Error('Formato de fecha check-out inválido');
      }
    } catch (error) {
      console.error('❌ Error convirtiendo fechas:', error);
      return;
    }

    const waitingListData: WaitingListFormData = {
      patient: patient.patient_id,
      desired_check_in: checkInStr,
      desired_check_out: checkOutStr,
      priority: formValue.priority,
      notes: formValue.notes || '',
    };

    console.log('💳 Datos de waiting list a enviar:', waitingListData);
    console.log('📤 JSON a enviar:', JSON.stringify(waitingListData, null, 2));

    this.formSubmit.emit(waitingListData);
  }

  onCancel(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetForm();
  }

  onHide(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  resetForm(): void {
    this.waitingListForm.reset({
      patient: null,
      desired_check_in: '',
      desired_check_out: '',
      priority: 2,
      notes: '',
    });
  }
}
